import { get } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import and from 'ember-truth-helpers/helpers/and';
import eq from 'ember-truth-helpers/helpers/eq';
import not from 'ember-truth-helpers/helpers/not';
import or from 'ember-truth-helpers/helpers/or';
import BvBtn from 'volta/components/bv-btn';
import BvIcon from 'volta/components/bv-icon';
import BvSpinner from 'volta/components/bv-spinner';

import type { ContentValue } from '@glint/template';
import type { Select } from 'ember-power-select/components/power-select';
import type { IAlignment, IBtnColor, TButtonSize } from 'volta/components/bv-btn';

export interface BvSelectTriggerSignature {
  Args: {
    verticalPosition?: 'auto' | 'above' | 'below';
    hideCaret?: boolean;
    allowClear?: boolean;
    prefix?: ContentValue;
    suffix?: ContentValue;
    placeholder?: boolean;
    selectedPath?: string;
    isLoading?: boolean;
    disabled?: boolean;
    focused?: boolean;
    alignText?: IAlignment;
    fill?: boolean;
    outline?: boolean;
    rounded?: boolean;
    plain?: boolean;
    subtle?: boolean;
    size?: TButtonSize;
    tooltipText?: string;
    color?: IBtnColor;
    iconRight?: string;
    iconLeft?: string;
    select?: Select;
  };
  Blocks: {
    default: [unknown, unknown];
  };
}

export default class BvSelectTriggerComponent extends Component<BvSelectTriggerSignature> {
  <template>
    <BvBtn
      class='bv-select__trigger-btn'
      @iconLeft={{@iconLeft}}
      @iconRight={{@iconRight}}
      @color={{@color}}
      @tooltipText={{@tooltipText}}
      @size={{@size}}
      @subtle={{@subtle}}
      @plain={{@plain}}
      @rounded={{@rounded}}
      @outline={{@outline}}
      @fullWidth={{@fill}}
      @alignText={{@alignText}}
      @disabled={{or @disabled @isLoading}}
    >
      {{#if @prefix}}
        <span class='bv-select__inline-label'>
          {{@prefix}}
        </span>
      {{/if}}
      {{#if @select.selected}}
        <span class='bv-select__selected-option'>
          {{#if @selectedPath}}
            {{get @select.selected @selectedPath}}
          {{else}}
            {{yield @select.selected @select}}
          {{/if}}
        </span>
      {{else}}
        <span class='bv-select__placeholder'>
          {{@placeholder}}
        </span>
      {{/if}}
      {{#if @suffix}}
        <span class='bv-select__inline-label u-ml-1'>
          {{@suffix}}
        </span>
      {{/if}}
      {{#if (and @select.selected @allowClear (not @select.disabled))}}
        <BvIcon
          class='bv-select__clear bv-btn__icon'
          @glyph='close-circle'
          @size={{@size}}
          @onClick={{this.clear}}
        />
      {{/if}}
      <div class='bv-select__trigger-caret bv-btn__icon'>
        {{#if @isLoading}}
          <BvSpinner @size='xs' />
        {{else}}
          {{#unless @hideCaret}}
            <BvIcon
              @glyph={{if
                (eq @verticalPosition 'above')
                'caret-up'
                (if (eq @verticalPosition 'below') 'caret-down' 'select')
              }}
              @size={{if (or (eq @size 'md') (not @size)) 'sm' @size}}
            />
          {{/unless}}
        {{/if}}
      </div>
    </BvBtn>
  </template>
  @action
  clear(e: Event) {
    e.stopPropagation();
    this.args.select?.actions.select(null);
    return e.type !== 'touchstart';
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvSelect::Trigger': typeof BvSelectTriggerComponent;
    'bv-select/trigger': typeof BvSelectTriggerComponent;
  }
}
